class CategoriesDropdown extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this._menuPortal = null;
  }

  connectedCallback() {
    this.button = this.querySelector('.categories-dropdown__button');
    this.menu = this.querySelector('.categories-dropdown__menu');
    this.links = this.querySelectorAll('.categories-dropdown__link');

    if (!this.button || !this.menu) return;

    this.button.addEventListener('click', () => this.toggleMenu());

    this.links.forEach(link => {
      link.addEventListener('click', () => this.closeMenu());
    });

    document.addEventListener('click', (e) => this.handleClickOutside(e));

    this.button.addEventListener('keydown', (e) => this.handleKeyboard(e));
    this.menu.addEventListener('keydown', (e) => this.handleMenuKeyboard(e));

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeMenu();
        this.button.focus();
      }
    });

    window.addEventListener('scroll', () => {
      if (this.isOpen) this.positionMenu();
    }, { passive: true });

    window.addEventListener('resize', () => {
      if (this.isOpen) this.positionMenu();
    });

    this._pushToRightEdge();
  }

  _pushToRightEdge() {
    const flexContainer = this.closest('.collection-header-layout');
    if (!flexContainer) return;

    // Walk up from this element to find the direct child of the flex container.
    // In production blocks render directly, in Shopify editor they're wrapped in
    // an extra div — either way we find the true flex item.
    let flexItem = this;
    while (flexItem.parentElement && flexItem.parentElement !== flexContainer) {
      flexItem = flexItem.parentElement;
    }

    flexContainer.style.width = '100%';
    flexContainer.style.maxWidth = '100%';
    flexContainer.style.marginInlineEnd = '0';

    flexItem.style.marginLeft = 'auto';
    flexItem.style.width = 'auto';
    flexItem.style.maxWidth = 'none';
    flexItem.style.flexShrink = '0';
  }

  toggleMenu() {
    if (this.isOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  positionMenu() {
    const buttonRect = this.button.getBoundingClientRect();
    const portal = this._menuPortal || this.menu;
    portal.style.top = (buttonRect.bottom + window.scrollY + 8) + 'px';
    portal.style.left = (buttonRect.left + window.scrollX) + 'px';
  }

  openMenu() {
    this.isOpen = true;
    this.setAttribute('data-open', 'true');
    this.button.setAttribute('aria-expanded', 'true');

    // Portal: move menu to <body> to escape overflow:clip on .kt-section
    if (!this._menuPortal) {
      this._menuPortal = this.menu.cloneNode(true);
      this._menuPortal.classList.add('categories-dropdown__menu--portal');
      document.body.appendChild(this._menuPortal);

      // Re-bind link click listeners on the portal copy
      this._menuPortal.querySelectorAll('.categories-dropdown__link').forEach(link => {
        link.addEventListener('click', () => this.closeMenu());
      });
      this._menuPortal.addEventListener('keydown', (e) => this.handleMenuKeyboard(e));
    }

    this._menuPortal.style.display = 'block';
    this._menuPortal.style.position = 'absolute';
    this._menuPortal.style.zIndex = '99999';
    this._menuPortal.style.opacity = '1';
    this._menuPortal.style.visibility = 'visible';
    this.positionMenu();

    const firstLink = this._menuPortal.querySelector('.categories-dropdown__link');
    if (firstLink) {
      setTimeout(() => firstLink.focus(), 50);
    }
  }

  closeMenu() {
    this.isOpen = false;
    this.setAttribute('data-open', 'false');
    this.button.setAttribute('aria-expanded', 'false');
    if (this._menuPortal) {
      this._menuPortal.style.display = 'none';
    }
  }

  handleClickOutside(e) {
    const clickedPortal = this._menuPortal && this._menuPortal.contains(e.target);
    if (!this.contains(e.target) && !clickedPortal && this.isOpen) {
      this.closeMenu();
    }
  }

  handleKeyboard(e) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!this.isOpen) {
        this.openMenu();
      } else {
        const links = this._menuPortal
          ? this._menuPortal.querySelectorAll('.categories-dropdown__link')
          : this.links;
        const idx = e.key === 'ArrowDown' ? 0 : links.length - 1;
        if (links[idx]) links[idx].focus();
      }
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this.toggleMenu();
    }
  }

  handleMenuKeyboard(e) {
    const links = this._menuPortal
      ? Array.from(this._menuPortal.querySelectorAll('.categories-dropdown__link'))
      : Array.from(this.links);
    const currentIndex = links.indexOf(document.activeElement);

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = currentIndex === -1 ? 0 : Math.min(currentIndex + 1, links.length - 1);
      if (links[next]) links[next].focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (currentIndex <= 0) {
        this.button.focus();
      } else {
        if (links[currentIndex - 1]) links[currentIndex - 1].focus();
      }
    } else if (e.key === 'Home') {
      e.preventDefault();
      if (links[0]) links[0].focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      if (links[links.length - 1]) links[links.length - 1].focus();
    }
  }
}

if (!customElements.get('categories-dropdown')) {
  customElements.define('categories-dropdown', CategoriesDropdown);
}
